import type { ReactElement, ReactNode } from "react";
import { Children, useMemo } from "react";

import { Link } from "react-router";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { Icon } from "../icon";
import {
  type UiColorPalette,
  UIProvider,
  useUiColor,
  useUIOptional,
} from "../utils/UIContext";
import { CONTROL_HEIGHT } from "../utils/colors";
import { isElementOfType } from "../utils/elementType";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";
import { LABEL_FONT_SIZE } from "./Label.constants";
import type { LabelProps, LabelSize } from "./Label.types";
import type { LabelLinkProps } from "./LabelLink";

function isIconChild(child: ReactNode): boolean {
  return isElementOfType(child, Icon);
}

const toControlHeight = (size?: LabelSize) => {
  if (size === "small") {
    return "20px";
  }
  if (size === "medium") {
    return CONTROL_HEIGHT.small;
  }
  if (size === "large") {
    return CONTROL_HEIGHT.medium;
  }
  return CONTROL_HEIGHT.small;
};

type StyleProps = {
  $color: UiColorPalette;
  $basic: boolean;
  $padding: string;
  $size?: LabelSize;
  $borderRadius: string;
  $controlHeight: string;
};

export const StyledLabel = styled.div<StyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  vertical-align: middle;
  background-color: ${({ $basic, $color }) =>
    $basic ? "#ffffff" : $color.solid};
  border: ${({ $basic, $color }) =>
    $basic ? `1px solid ${$color.strong}` : "none"};
  color: ${({ $basic, $color }) => ($basic ? $color.ink : $color.onSolid)};
  padding: ${({ $padding }) => $padding};
  font-size: ${({ $size }) =>
    ($size && LABEL_FONT_SIZE[$size]) || LABEL_FONT_SIZE.medium};
  height: ${({ $controlHeight }) => $controlHeight};
  font-weight: ${({ $basic }) => ($basic ? "normal" : "bold")};
  width: fit-content;
  border-radius: ${({ $borderRadius }) => $borderRadius};
  box-sizing: border-box;
  cursor: default;
  position: relative;
  white-space: nowrap;
  text-decoration: none;

  a {
    color: ${({ $basic, $color }) => ($basic ? $color.ink : $color.onSolid)};
  }

  &.is-circular {
    justify-content: center;
    min-width: ${({ $controlHeight }) => $controlHeight};
  }

  &.is-icon > ${Icon} {
    margin: 0 !important;
  }

  &:not(.is-icon) > ${Icon}:first-child {
    margin-right: 0.3rem;
  }

  &.is-small {
    ${Icon} {
      width: 10px;
      height: 10px;
    }
  }

  &.is-empty {
    padding: 0;
    height: ${({ $controlHeight }) => `calc(${$controlHeight} - 8px)`};
    width: ${({ $controlHeight }) => `calc(${$controlHeight} - 8px)`};
    min-height: ${({ $controlHeight }) => `calc(${$controlHeight} - 8px)`};
    min-width: ${({ $controlHeight }) => `calc(${$controlHeight} - 8px)`};
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-ribbon {
    left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &.is-ribbon-right {
    left: calc(100% + 1px);
    text-align: left;
    transform: translateX(-100%);
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.is-pointing-above {
    margin-top: 1em;
    &::before {
      content: "";
      position: absolute;
      top: -0.35em;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
      width: 0.6em;
      height: 0.6em;
      background: inherit;
      border: inherit;
    }
  }

  &.is-pointing-below {
    margin-bottom: 1em;
    &::before {
      content: "";
      position: absolute;
      bottom: -0.35em;
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
      width: 0.6em;
      height: 0.6em;
      background: inherit;
    }
  }

  &.is-pointing-left {
    margin-left: 0.6em;
    &::before {
      content: "";
      position: absolute;
      left: -0.3em;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      width: 0.6em;
      height: 0.6em;
      background: inherit;
      border-bottom: inherit;
      border-left: inherit;
      border-top: none;
      border-right: none;
      z-index: 1;
    }
  }

  &.is-pointing-right {
    margin-right: 0.6em;
    &::before {
      content: "";
      position: absolute;
      right: -0.3em;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      width: 0.6em;
      height: 0.6em;
      background: inherit;
      border-top: inherit;
      border-right: inherit;
      border-bottom: none;
      border-left: none;
      z-index: 1;
    }
  }
`;

function labelPadding(size?: LabelSize): string {
  if (size === "small") {
    return "0.4em 0.65em";
  }
  return "0.6em 0.85em";
}

function labelBorderRadius(circular?: boolean): string {
  if (circular) {
    return "500rem";
  }

  return "0.3rem";
}

const LabelLook = ({
  color: _color,
  basic,
  circular,
  ribbon,
  pointing,
  size,
  children,
  className,
  style,
  onClick,
  asLink,
  ...rest
}: (LabelProps | LabelLinkProps) & { asLink?: boolean }) => {
  const empty = children === undefined;
  const body = children;
  const ui = useUIOptional();
  const uiColor = useUiColor();

  const childArray = Children.toArray(children);
  const iconOnly = childArray.length === 1 && isIconChild(childArray[0]);

  const pointingAbove = pointing === true || pointing === "above";
  const pointingBelow = pointing === "below";
  const pointingLeft = pointing === "left";
  const pointingRight = pointing === "right";

  const controlHeight = toControlHeight(size || ui?.size);

  return (
    <StyledLabel
      as={asLink ? Link : undefined}
      className={cx(
        (onClick || asLink) && "is-clickable",
        `is-${size || ui?.size || "medium"}`,
        empty && "is-empty",
        iconOnly && "is-icon",
        circular && "is-circular",
        ribbon === true && "is-ribbon",
        ribbon === "right" && "is-ribbon-right",
        pointingAbove && "is-pointing-above",
        pointingBelow && "is-pointing-below",
        pointingLeft && "is-pointing-left",
        pointingRight && "is-pointing-right",
        className,
      )}
      style={style}
      onClick={onClick}
      $controlHeight={controlHeight}
      $color={uiColor}
      $basic={Boolean(basic)}
      $padding={labelPadding(size || ui?.size)}
      $size={size}
      $borderRadius={labelBorderRadius(circular)}
      {...(rest as object)}
    >
      {[empty ? null : body]}
    </StyledLabel>
  );
};

export const RenderLabel = (
  props: (LabelProps | LabelLinkProps) & { asLink?: boolean },
): ReactElement => {
  const theme = useTheme();
  const { color = "grey" } = props;

  const palette = useMemo(
    () => resolveUiPalette(theme, color as UiPaletteKey),
    [color, theme],
  );

  return (
    <UIProvider value={{ colors: palette, size: props.size }}>
      <LabelLook {...props} />
    </UIProvider>
  );
};

const LabelComponent = (props: LabelProps): ReactElement => (
  <RenderLabel {...props} />
);

export const Label = styled(LabelComponent)``;
